import React, { useCallback, useMemo } from 'react';
import { RovoAiProjectModalListener } from '@jira/servicedesk__solution-composer/src/RovoAiProjectModalListener.tsx';
import { MODAL_ID } from '@jira/navigation-apps__atlassian-navigation-ui/src/constants.tsx';
import { getProjectsNavigationPaths } from '@jira/navigation-apps__sidebar-common/src/utils/get-navigation-path/projects/index.tsx';
import { ChangeViewMenuItem } from '@jira/navigation-apps__sidebar-nav4-display-settings/src/ui/change-view-menu-item/index.tsx';
import { ViewOptions } from '@jira/navigation-apps__sidebar-nav4-display-settings/src/ui/change-view-menu-item/view-options/constants.tsx';
import type { ViewOptionsType } from '@jira/navigation-apps__sidebar-nav4-display-settings/src/ui/change-view-menu-item/view-options/types.tsx';
import { useProjectsAnalyticsL1 } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l1/index.tsx';
import { useProjectsAnalyticsL2 } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l2/index.tsx';
import { useProjectsAnalyticsL3 } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l3/index.tsx';
import {
	STARRED_AND_RECENT_PROJECTS,
	STARRED_PROJECTS_ONLY,
	MOST_RECENT_PROJECT_ONLY,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/item-id.tsx';
import {
	type OnCloseProps,
	L1_MENU_ID,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { CommonNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/common-nav-menu-button/main.tsx';
import { useStopPropagation } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-stop-propagation/index.tsx';
import { HideL1MenuItemSection } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/hide-l1-menu-item-action/index.tsx';
import { MoreNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import type { MoreNavMenuButtonMenuItemsProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/submenu/index.tsx';
import { useSwitchModals } from '@jira/platform__controllers-switch-modals/src/index.tsx';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { token } from '@atlaskit/tokens';
import LinkItem from '@atlaskit/menu/link-item';
import Section from '@atlaskit/menu/section';
import MenuGroup from '@atlaskit/menu/menu-group';
import AddIcon from '@atlaskit/icon/core/add';
import SettingsIcon from '@atlaskit/icon/core/settings';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';

const createProject = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-core.common.action-buttons.create-project',
	defaultMessage: 'Create project',
	description: 'Used as the alt text for an icon button that when used navigates to create project',
});

const manageProjects = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-core.common.action-buttons.manage-projects',
	defaultMessage: 'Manage projects',
	description: 'Used as the menu-title for the Manage Projects menu link item',
});

const projects = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-core.common.action-buttons.projects',
	defaultMessage: 'projects',
	description: 'Used as the menu-title for the Projects more actions item',
});

const createProjectGalaxiaMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-core.common.action-buttons.create-project-galaxia',
	defaultMessage: 'Create space',
	description: 'Used as the alt text for an icon button that when used navigates to create project',
});

const manageProjectsGalaxiaMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-core.common.action-buttons.manage-projects-galaxia',
	defaultMessage: 'Manage spaces',
	description: 'Used as the menu-title for the Manage Projects menu link item',
});

const projectsGalaxiaMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-core.common.action-buttons.projects-galaxia',
	defaultMessage: 'spaces',
	description: 'Used as the menu-title for the Projects more actions item',
});

type ActionButtonsProps = {
	canCreateProject: boolean;
	shouldDisplayCreateProject?: boolean;
} & OnCloseProps;

const CREATE_PROJECT_BUTTON_TEST_ID =
	'navigation-apps-sidebar-nav4-sidebars-content-projects-core.common.ui.action-buttons.create-project-button';

export function ActionButtons({
	onClose,
	canCreateProject,
	shouldDisplayCreateProject,
}: ActionButtonsProps): React.JSX.Element {
	const projectsGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? projectsGalaxiaMessage
		: projects;
	const createProjectGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? createProjectGalaxiaMessage
		: createProject;
	const { formatMessage } = useIntl();
	const [, { on: openProjectCreateDrawer }] = useSwitchModals(MODAL_ID.PROJECT_CREATE);
	const { sendMeatBallDropDownAnalyticsEvent, sendCreateProjectAnalyticsEvent } =
		useProjectsAnalyticsL1();

	const onClick = useCallback(() => {
		openProjectCreateDrawer();
		sendCreateProjectAnalyticsEvent();
	}, [openProjectCreateDrawer, sendCreateProjectAnalyticsEvent]);

	const onOpen = useCallback(() => {
		sendMeatBallDropDownAnalyticsEvent();
	}, [sendMeatBallDropDownAnalyticsEvent]);

	const showProjectCreateButton = fg('allow_end_users_to_access_custom_templates')
		? shouldDisplayCreateProject
		: canCreateProject;

	const createButton = showProjectCreateButton ? (
		<Tooltip content={formatMessage(createProjectGalaxia)}>
			<CommonNavMenuButton
				icon={(iconProps) => <AddIcon {...iconProps} size="small" />}
				label={formatMessage(createProjectGalaxia)}
				onClick={onClick}
				{...(fg('solution_composer_master') ? { testId: CREATE_PROJECT_BUTTON_TEST_ID } : {})}
			/>
		</Tooltip>
	) : null;

	return (
		<>
			{createButton}
			{fg('solution_composer_master') && <RovoAiProjectModalListener />}
			<MoreNavMenuButton
				name={formatMessage(projectsGalaxia)}
				onOpen={onOpen}
				MenuItems={MoreMenuItems}
				onClose={onClose}
			/>
		</>
	);
}

function MoreMenuItems({ onClose }: MoreNavMenuButtonMenuItemsProps) {
	const manageProjectsGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? manageProjectsGalaxiaMessage
		: manageProjects;
	const { formatMessage } = useIntl();
	const href = useMemo(() => getProjectsNavigationPaths().projectsViewProjects, []);

	const { sendMeatBallDropDownManageProjectsAnalyticsEvent, sendChangeViewAnalyticsEvent } =
		useProjectsAnalyticsL2();

	const { sendChangeViewViewOptionAnalyticsEvent } = useProjectsAnalyticsL3();

	const onCloseWithStopPropagation = useStopPropagation(
		(e: Event | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>) => {
			sendMeatBallDropDownManageProjectsAnalyticsEvent();
			onClose?.(e);
		},
	);

	const onChangeViewOptionChange = useCallback(
		(viewOptionValue: ViewOptionsType) => {
			const getItemId = () => {
				switch (viewOptionValue) {
					case ViewOptions.STARRED:
						return STARRED_PROJECTS_ONLY;
					case ViewOptions.STARRED_AND_RECENT:
						return STARRED_AND_RECENT_PROJECTS;
					case ViewOptions.MOST_RECENT_ONLY:
						return MOST_RECENT_PROJECT_ONLY;
					default:
						return STARRED_AND_RECENT_PROJECTS;
				}
			};
			sendChangeViewViewOptionAnalyticsEvent({ itemId: getItemId() });
		},
		[sendChangeViewViewOptionAnalyticsEvent],
	);

	return (
		<MenuGroup>
			<Section isList>
				<LinkItem
					iconBefore={<SettingsIcon label="" spacing="spacious" color={token('color.icon')} />}
					href={href}
					onClick={onCloseWithStopPropagation}
				>
					{formatMessage(manageProjectsGalaxia)}
				</LinkItem>
			</Section>
			<Section hasSeparator isList>
				<ChangeViewMenuItem
					onOpen={sendChangeViewAnalyticsEvent}
					onChange={onChangeViewOptionChange}
				/>
				{!fg('navx-global-side-nav') && (
					<HideL1MenuItemSection
						hasSeparator={false}
						menuId={L1_MENU_ID.PROJECTS}
						hasSection={false}
					/>
				)}
			</Section>
		</MenuGroup>
	);
}
